import React from 'react';
import { TableColumn } from 'tea-component';
import ColumnsCenter from '@intelligence/components/TableComponents/components/CloumsCenter';
import FilterTagCellList, { IFilterTagItem } from '@intelligence/v3/utils/filterTagCellList';
import {
  APPMAGIC_TAG_KEYS,
  APPMAGIC_TAG_TITLE_MAP,
  SENSOR_TOWER_TAG_KEYS,
  SENSOR_TOWER_TAG_MAP,
  SENSOR_TOWER_TAG_THEME,
  SENSOR_TOWER_TAG_TITLE_MAP,
} from '@intelligence/components/Filter/FilterBase/_config';
import LOCALE_EN from '@intelligence/_locales/en-US/common';
import { getStringArrayValue } from '@intelligence/v3/filterControls/shared/valueUtils';
import { COMMON_JOINER, generateDataSourceMetricKey } from '@intelligence/utils/topchartsUtils';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { LANGUAGE_CN } from '@home/_config/config';

import type { ISensorTowerTag } from '@intelligence/types/topCharts';
import { getGameInfoByIdPlus } from '@intelligence/services/common';

import '@intelligence/components/TableComponents/components/TableGenre/index.scss';

/** 表格 Sensor Tower / App Magic 动态标签列所需的游戏详情字段（与 useTopChartsCore 一致） */
export const MOBILE_TOPCHARTS_TAG_DETAIL_FIELDS = ['sensortower_tag', 'app_magic_tags'];

export const resolveTableRecordGameId = (cvm: any): string =>
  cvm?.game_id ||
  cvm?.id ||
  cvm?.app_id ||
  cvm?.unifiedId ||
  cvm?.unified_id ||
  '';

export async function fetchMobileTopChartsTagDetailsMapping(
  ids: string[],
): Promise<IRecord<IRecord>> {
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  if (uniqueIds.length === 0) {
    return {};
  }

  const res = await getGameInfoByIdPlus({
    entity_type: 'mobile',
    ids: uniqueIds,
    level: 'custom',
    custom_fields: MOBILE_TOPCHARTS_TAG_DETAIL_FIELDS,
  });

  return res?.data || {};
}

interface ITagFilterColumnParams {
  filterValue?: IRecord<any>;
  gameDetailsLoading?: boolean;
  gameDetailsMapping?: IRecord<IRecord>;
  locale?: string;
  formatMessage: (descriptor: { id: string }) => string;
  onTagClick?: (filterKey: string, clickValue: string) => void;
  width?: number;
  placeholder?: string;
}

const DEFAULT_WIDTH = 160;
const DEFAULT_PLACEHOLDER = '-';

const getFilterTagValues = (filterValue: IRecord<any>, filterKey: string): string[] => {
  const item = filterValue?.[filterKey];
  return getStringArrayValue(Array.isArray(item) ? item : item?.value);
};

const getTagDisplayText = (tag: ISensorTowerTag, locale?: string): string => {
  const isCn = locale === LANGUAGE_CN;
  return (isCn ? tag?.tag_name_ch : tag?.tag_name) || tag?.tag_name || tag?.tag_name_ch || '';
};

const findTagCategory = (
  tagCategories: ISensorTowerTag[] | undefined,
  categoryName?: string,
): ISensorTowerTag | undefined => {
  return categoryName && Array.isArray(tagCategories)
    ? tagCategories.find((item) => item?.tag_name === categoryName)
    : undefined;
};

const flattenSensorTowerCategoryTags = (
  category: ISensorTowerTag | undefined,
  filterKey: string,
  locale?: string,
): IFilterTagItem[] => {
  if (!category?.Tags?.length) {
    return [];
  }

  const isTheme = filterKey === SENSOR_TOWER_TAG_THEME;
  const result: IFilterTagItem[] = [];

  const walk = (tags: ISensorTowerTag[], parentPath?: string) => {
    tags.forEach((tag) => {
      const tagName = tag?.tag_name;
      if (!tagName) {
        return;
      }

      const subTags = tag.Tags;
      const hasSubTags = Array.isArray(subTags) && subTags.length > 0;

      if (isTheme && hasSubTags) {
        const currentPath = parentPath
          ? generateDataSourceMetricKey(parentPath, tagName, COMMON_JOINER)
          : tagName;

        result.push({ clickValue: currentPath, displayText: getTagDisplayText(tag, locale) });
        walk(subTags, currentPath);
        return;
      }

      result.push({
        clickValue:
          isTheme && parentPath
            ? generateDataSourceMetricKey(parentPath, tagName, COMMON_JOINER)
            : tagName,
        displayText: getTagDisplayText(tag, locale),
      });
    });
  };

  walk(category.Tags);
  return result;
};

const flattenAppMagicCategoryTags = (
  category: ISensorTowerTag | undefined,
  locale?: string,
): IFilterTagItem[] => {
  if (!category?.Tags?.length) {
    return [];
  }

  const result: IFilterTagItem[] = [];

  const walk = (tags: ISensorTowerTag[]) => {
    tags.forEach((tag) => {
      const tagName = tag?.tag_name;
      if (!tagName) {
        return;
      }

      const subTags = tag.Tags;
      if (Array.isArray(subTags) && subTags.length > 0) {
        walk(subTags);
        return;
      }

      result.push({
        clickValue: tagName,
        displayText: getTagDisplayText(tag, locale),
      });
    });
  };

  walk(category.Tags);
  return result;
};

/** 主题一级 tag 需展开为 Cascader 叶子 value，才能等价于筛选器全选 */
export const resolveSensorTowerTagFilterValues = (
  filterKey: string,
  clickValue: string,
  sensorTowerTagConfig?: IRecord<any[]>,
): string[] => {
  if (!clickValue) {
    return [];
  }

  if (filterKey !== SENSOR_TOWER_TAG_THEME || clickValue.includes(COMMON_JOINER)) {
    return [clickValue];
  }

  const themeOptions = sensorTowerTagConfig?.[SENSOR_TOWER_TAG_THEME];
  const parentOption = Array.isArray(themeOptions)
    ? themeOptions.find((option) => option?.value === clickValue)
    : undefined;
  const leafValues = getStringArrayValue(
    parentOption?.children?.map((child: { value?: string }) => child?.value),
  );

  return leafValues.length > 0 ? leafValues : [clickValue];
};

const createTagFilterColumns = (config: {
  filterKeys: string[];
  titleMap: IRecord<string>;
  categoryMap: IRecord<string>;
  detailField: 'sensortower_tag' | 'app_magic_tags';
  /** 列头数据源 icon，如 sensortower / appmagic */
  dataSourceIconCode: string;
  flattenTags: (
    category: ISensorTowerTag | undefined,
    filterKey: string,
    locale?: string,
  ) => IFilterTagItem[];
}) => {
  return (params: ITagFilterColumnParams): TableColumn<any>[] => {
    const {
      filterValue,
      gameDetailsLoading = true,
      gameDetailsMapping,
      locale,
      formatMessage,
      onTagClick,
      width = DEFAULT_WIDTH,
      placeholder = DEFAULT_PLACEHOLDER,
    } = params;
    const headerIcon = getDataSourceIcon(config.dataSourceIconCode);

    return config.filterKeys
      .filter((filterKey) => getFilterTagValues(filterValue || {}, filterKey).length > 0)
      .map((filterKey) => ({
        key: filterKey,
        align: 'left' as const,
        width,
        header: (
          <ColumnsCenter width={width} align="left">
            <div className="general_table_genre-header">
              {headerIcon ? <img className="header-icon" src={headerIcon} alt="" /> : null}
              <span>{formatMessage({ id: config.titleMap[filterKey] })}</span>
            </div>
          </ColumnsCenter>
        ),
        render: (cvm: any) => {
          if (gameDetailsLoading) {
            return (
              <ColumnsCenter width={width} align="left">
                {placeholder}
              </ColumnsCenter>
            );
          }

          const gameId = resolveTableRecordGameId(cvm);
          const tagCategories = gameDetailsMapping?.[gameId]?.[config.detailField] as
            | ISensorTowerTag[]
            | undefined;
          const category = findTagCategory(tagCategories, config.categoryMap[filterKey]);
          const tagItems = config.flattenTags(category, filterKey, locale);

          return (
            <ColumnsCenter width={width} align="left">
              {tagItems.length > 0 ? (
                <FilterTagCellList tags={tagItems} filterKey={filterKey} onTagClick={onTagClick} />
              ) : (
                placeholder
              )}
            </ColumnsCenter>
          );
        },
      }));
  };
};

const getSensorTowerTagColumns = createTagFilterColumns({
  filterKeys: SENSOR_TOWER_TAG_KEYS,
  titleMap: SENSOR_TOWER_TAG_TITLE_MAP,
  categoryMap: SENSOR_TOWER_TAG_MAP,
  detailField: 'sensortower_tag',
  dataSourceIconCode: 'sensortower',
  flattenTags: flattenSensorTowerCategoryTags,
});

const getAppMagicTagColumns = createTagFilterColumns({
  filterKeys: APPMAGIC_TAG_KEYS,
  titleMap: APPMAGIC_TAG_TITLE_MAP,
  categoryMap: APPMAGIC_TAG_KEYS.reduce<IRecord<string>>((map, key) => {
    map[key] = LOCALE_EN[APPMAGIC_TAG_TITLE_MAP[key] as keyof typeof LOCALE_EN] || '';
    return map;
  }, {}),
  detailField: 'app_magic_tags',
  dataSourceIconCode: 'appmagic',
  flattenTags: (category, _filterKey, locale) => flattenAppMagicCategoryTags(category, locale),
});

/** Sensor Tower + App Magic 动态标签列（ST 在前，App Magic 在后） */
export const getTagFilterColumns = (params: ITagFilterColumnParams): TableColumn<any>[] => [
  ...getSensorTowerTagColumns(params),
  ...getAppMagicTagColumns(params),
];

interface IFilterRef {
  setValueAndConfirm?: (value: IRecord<any>) => void;
}

interface IBuildTopChartsTagClickHandlerOptions {
  filterRef?: { current: IFilterRef | null };
  filterValue?: IRecord<any>;
  setFilterValue?: (value: IRecord<any>) => void;
  sensorTowerTagConfig?: IRecord<any[]>;
  onFallbackConfirm?: (patch: IRecord<any>) => void;
}

/** 表格标签点击：回填 Sensor Tower / App Magic 筛选器（ActiveUsers 同款逻辑） */
export const buildTopChartsTagClickHandler = (
  options: IBuildTopChartsTagClickHandlerOptions,
) => {
  return (type: string, tag: string): void => {
    if (
      !SENSOR_TOWER_TAG_KEYS.includes(type) &&
      !APPMAGIC_TAG_KEYS.includes(type)
    ) {
      return;
    }

    const filterValues = SENSOR_TOWER_TAG_KEYS.includes(type)
      ? resolveSensorTowerTagFilterValues(type, tag, options.sensorTowerTagConfig)
      : [tag];

    const patch = { [type]: { value: filterValues } };

    if (options.filterRef?.current?.setValueAndConfirm) {
      options.filterRef.current.setValueAndConfirm(patch);
      return;
    }

    const nextFilterValue = {
      ...(options.filterValue || {}),
      ...patch,
    };
    options.setFilterValue?.(nextFilterValue);
    options.onFallbackConfirm?.(patch);
  };
};
